import React, { useState } from "react";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import Image from "@components/item/Image";
import BasicDrawer from "@components/drawer/BasicDrawer";
import { useTranslation } from "react-i18next";
import SwiperContainer from "@components/swiper/SwiperContainer";
import { SwiperSlide } from "swiper/react";

const Container = styled.div({
  display: "flex",
  gap: "1.6rem",
  width: "100%",
  maxWidth: "560px",
});

const FilterWrapper = styled.div({
  display: "flex",
  gap: "1.2rem",
  alignItems: "center",
  width: "100%",
  maxWidth: "560px",
});

const FilterLabelButton = styled.button(({ theme }) => ({
  display: "flex",
  textWrap: "nowrap",
  gap: "0.4rem",
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "1.6rem",
  padding: "0.8rem 0 0.8rem 0",
}));

const FilterButton = styled(SwiperSlide)<{ focus: boolean }>(
  ({ theme, focus }) => ({
    maxWidth: "3.6rem",
    display: "flex",
    justifyContent: "center",
    cursor: "pointer",
    color: focus ? theme.color.green : theme.color.black,
  }),
);

const Bar = styled.div(({ theme }) => ({
  width: "1px",
  height: "2rem",
  backgroundColor: theme.color.light_gray,
}));

const FilterItem = styled.button<{ isLast: boolean; focus: boolean }>(
  ({ theme, isLast, focus }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.medium,
    color: focus ? theme.color.green : theme.color.black,
    lieneHeight: "1.6rem",
    cursor: "pointer",
    padding: "2rem 0",
    borderBottom: isLast ? "none" : `1px solid ${theme.color.border_gray}`,
  }),
);

type Props = {
  selectedFilter: string;
  setSelectedFilter: (value: string) => void;
  filterList: any[];
  selectedFilterDetail: string;
  setSelectedFilterDetail: (value: string) => void;
  selectedFilterList: { id: number; label: string }[];
};
const BrandFilter = ({
  selectedFilter,
  setSelectedFilter,
  filterList,
  selectedFilterDetail,
  setSelectedFilterDetail,
  selectedFilterList,
}: Props) => {
  const theme = useTheme();
  const [openDrawer, setOpenDrawer] = useState(false);
  const { t } = useTranslation();
  const lang = window.localStorage.getItem("language");
  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleFiltering = (label: string) => {
    setSelectedFilterDetail(label);
  };

  const getTranslatedFilter = (filter: string, lang: string | null) => {
    if (lang !== "en") return filter;

    const translations: Record<string, string> = {
      "ABC 순": "Alphabetical Order",
      "가나다 순": "Korean Alphabetical Order",
      "최신 등록순": "Latest",
    };

    return translations[filter] || filter;
  };

  return (
    <Container>
      <FilterWrapper>
        <FilterLabelButton onClick={handleOpenDrawer}>
          {getTranslatedFilter(selectedFilter, lang)}
          <Image src={theme.image.arrow_down.default} size="1.6rem" />
        </FilterLabelButton>

        <Bar />

        {selectedFilter !== "최신 등록순" && (
          <SwiperContainer perView={"auto"}>
            <FilterButton
              onClick={() => handleFiltering("ALL")}
              focus={selectedFilterDetail === "ALL"}>
              ALL
            </FilterButton>
            {selectedFilterList.map((item, index) => (
              <FilterButton
                key={index}
                onClick={() => handleFiltering(item.label)}
                focus={item.label === selectedFilterDetail}>
                {item.label}
              </FilterButton>
            ))}
          </SwiperContainer>
        )}
      </FilterWrapper>

      <BasicDrawer open={openDrawer} setOpen={setOpenDrawer}>
        {filterList.map((filter, index) => (
          <FilterItem
            focus={filter.name_ko === selectedFilter}
            isLast={index === filterList.length - 1}
            key={filter.code}
            onClick={() => {
              setSelectedFilter(filter.name_ko);
              setSelectedFilterDetail("ALL");
              setOpenDrawer(false);
            }}>
            <span>{lang === "en" ? filter.name_en : filter.name_ko}</span>

            {filter.name_ko === selectedFilter && (
              <Image src={theme.image.check_green.default} />
            )}
          </FilterItem>
        ))}
      </BasicDrawer>
    </Container>
  );
};

export default BrandFilter;
